import {LockIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useEffect, useMemo} from 'react'
import type {AgentInfo, SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import type {SubAgent} from '@github-ui/agent-sessions-types/sub-agent'
import {hasModelsForVendors, isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import {GitHubAvatar} from '@github-ui/github-avatar'
import styles from './AgentListItems.module.css'

type AgentListItemsProps = {
  topLevelAgents: AgentInfo[]
  customAgents: SubAgent[]
  selectedAgent: SelectedAgent
  onSelectAgent: (agent: SelectedAgent) => void
}

export function AgentListItems({topLevelAgents, customAgents, selectedAgent, onSelectAgent}: AgentListItemsProps) {
  const {models = [], isLoading: modelsLoading, error: modelsError} = useAgentModels()

  const visibleCustomAgents = useMemo(
    () => customAgents.filter(agent => agent.user_invocable !== false),
    [customAgents],
  )

  // Filter top-level agents to exclude those whose vendor has no available models.
  // Skip filtering while models are loading or on error to avoid temporarily hiding all vendor agents.
  const visibleTopLevelAgents = useMemo(
    () =>
      modelsLoading || modelsError
        ? topLevelAgents
        : topLevelAgents.filter(agent => hasModelsForVendors(agent.model_vendors, models)),
    [topLevelAgents, models, modelsLoading, modelsError],
  )

  useEffect(() => {
    if (selectedAgent?.typeName === 'customSubAgent') {
      const isVisible = visibleCustomAgents.some(agent => agent.name === selectedAgent.subAgentSlug)
      if (!isVisible) {
        onSelectAgent({})
      }
    }
  }, [selectedAgent, visibleCustomAgents, onSelectAgent])

  return (
    <>
      {visibleTopLevelAgents.length > 0 && (
        <ActionList.Group selectionVariant="single">
          <ActionList.GroupHeading>Agents</ActionList.GroupHeading>
          {visibleTopLevelAgents.map((agent: AgentInfo) => {
            const isCopilot = isCopilotCodingAgent(agent.slug)
            const isTopLevelAgentSelected =
              selectedAgent?.typeName === 'topLevel' && selectedAgent.topLevelAgentId === agent.id
            const isCopilotDefaultSelected = !selectedAgent?.typeName && isCopilot
            const isSelected = isTopLevelAgentSelected || isCopilotDefaultSelected
            return (
              <ActionList.Item
                key={agent.id}
                disabled={false}
                selected={isSelected}
                onSelect={() =>
                  onSelectAgent(
                    isSelected
                      ? {}
                      : {
                          typeName: 'topLevel',
                          topLevelAgentId: agent.id,
                          displayName: agent.display_name,
                          avatarUrl: agent.avatar_url,
                          login: agent.slug,
                          botGlobalRelayId: agent.bot_global_relay_id,
                          modelVendors: agent.model_vendors,
                        },
                  )
                }
              >
                <ActionList.LeadingVisual>
                  <GitHubAvatar src={agent.avatar_url || ''} size={16} square />
                </ActionList.LeadingVisual>
                {agent.display_name}
                {agent.requires_authentication && (
                  <ActionList.TrailingVisual>
                    <span role="img" aria-label="Requires authorization" className={styles.authWarningIcon}>
                      <LockIcon size={14} />
                    </span>
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
        </ActionList.Group>
      )}

      {visibleCustomAgents.length > 0 && (
        <>
          {visibleTopLevelAgents.length > 0 && <ActionList.Divider />}
          <ActionList.Group selectionVariant="single">
            <ActionList.GroupHeading>Custom agents</ActionList.GroupHeading>
            {visibleCustomAgents.map(agent => {
              const hasConfigError = !!agent.config_error && agent.config_error.length > 0
              const isSelected =
                selectedAgent?.typeName === 'customSubAgent' && selectedAgent.subAgentSlug === agent.name
              return (
                <ActionList.Item
                  key={agent.name}
                  disabled={hasConfigError}
                  inactiveText={hasConfigError ? agent.config_error : undefined}
                  selected={isSelected}
                  onSelect={() =>
                    onSelectAgent(
                      isSelected
                        ? {}
                        : {
                            typeName: 'customSubAgent',
                            subAgentSlug: agent.name,
                            displayName: agent.display_name || agent.name,
                            login: agent.name,
                          },
                    )
                  }
                >
                  {agent.display_name || agent.name}
                </ActionList.Item>
              )
            })}
          </ActionList.Group>
        </>
      )}
    </>
  )
}
